import { generatePath as generatePathBase } from 'react-router-dom';
import type { Route } from './routes';

export function generatePath<Path extends Route>(path: Path, params: PathParams<Path>): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return generatePathBase(path, params as any);
}

/**
 * Regex patterns for Hydra-only URLs that don't require loading an app bundle.
 *
 * When adding a new Hydra-only route:
 * 1. Add the route to main.tsx
 * 2. Add a regex pattern here to match the route
 *
 * Ember can check if a URL belongs to Hydra by calling window.__hydra__.isHydraRoute(path).
 */
const HYDRA_ONLY_URLS_REGEX = [
  /^\/workspaces\/(last|(\d+))\/settings\/ai-business-profile/i,
  /^\/workspaces\/(last|(\d+))\/settings\/audit-logs/i,
  /^\/workspaces\/(last|(\d+))\/settings\/actions\/email\/account-verification/i,
  /^\/settings\/audit-logs/i,
  /^\/workspaces\/(last|(\d+))\/agent/i,
  /^\/workspaces\/(last|(\d+))\/tasks/i,
  /^\/workspaces\/(last|(\d+))\/analysis\/deliverability/i,
];

/**
 * Checks if a URL path matches a Hydra-only route.
 *
 * @example
 * isHydraRoute('/workspaces/123/agent') // true
 * isHydraRoute('/workspaces/last/settings/ai-business-profile') // true
 * isHydraRoute('/workspaces/123/foo') // false
 */
export function isHydraRoute(path: string): boolean {
  return HYDRA_ONLY_URLS_REGEX.some((regex) => regex.test(path));
}

/**
 * Magic types to extract path parameters from a route
 */
type ExtractPathParams<T extends string> = T extends `${infer _}:${infer Parameter}/${infer Rest}`
  ? Parameter | ExtractPathParams<Rest>
  : T extends `${infer _}:${infer Parameter}`
  ? Parameter
  : never;

type PathParams<T extends string> = {
  [K in ExtractPathParams<T>]: string | number;
};
